import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import MainContent from './components/MainContent';
import SearchPage from './components/SearchPage';
import LibraryPage from './components/LibraryPage';
import SettingsPage from './components/SettingsPage';
import PlayerPage from './components/PlayerPage';
import QueuePage from './components/QueuePage';
import MusicPlayer from './components/MusicPlayer';
import dataManager from './utils/dataManager';

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [librarySelectedPlaylist, setLibrarySelectedPlaylist] = useState('favorites');
  
  // 用户歌单状态管理 - 提升到App级别以保持同步
  const [userPlaylists, setUserPlaylists] = useState([]);
  
  // 播放队列状态管理
  const [playQueue, setPlayQueue] = useState([]);
  const [currentSongIndex, setCurrentSongIndex] = useState(-1);
  const [playMode, setPlayMode] = useState('list'); // list, repeat, shuffle, single
  
  // 页面历史记录管理
  const [pageHistory, setPageHistory] = useState(['home']);
  const [historyIndex, setHistoryIndex] = useState(0);
  
  // 当前播放的歌曲和播放状态
  const [currentPlayingSong, setCurrentPlayingSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [songInfo, setSongInfo] = useState(null);
  
  // 搜索状态管理 - 提升到App级别以保持状态
  const [searchState, setSearchState] = useState({
    keyword: '',
    selectedSource: 'aggregate',
    searchResults: [],
    isLoading: false,
    searchPage: 1,
    totalResults: 0,
    hasSearched: false
  });

  // 加载用户歌单数据
  useEffect(() => {
    loadPlaylists();
  }, []);

  const loadPlaylists = () => {
    const playlists = dataManager.getPlaylists();
    setUserPlaylists(playlists);
  };

  // 初始化播放队列和播放模式
  useEffect(() => {
    const initializePlayback = async () => {
      const savedQueue = dataManager.getPlayQueue();
      const savedIndex = dataManager.getCurrentIndex();
      const settings = dataManager.getSettings();
      
      setPlayQueue(savedQueue);
      setCurrentSongIndex(savedIndex);
      setPlayMode(settings.playMode || 'list');
      
      // 如果有保存的当前歌曲，恢复播放状态并获取详细信息
      if (savedQueue.length > 0 && savedIndex >= 0 && savedIndex < savedQueue.length) {
        const currentSong = savedQueue[savedIndex];
        setCurrentPlayingSong(currentSong);
        
        // 重新获取歌曲详细信息（播放链接、封面等）
        try {
          const response = await fetch(
            `https://music-dl.sayqz.com/api/?source=${currentSong.platform}&type=info&id=${currentSong.id}`
          );
          const data = await response.json();
          if (data.code === 200) {
            setSongInfo(data.data);
          }
        } catch (error) {
          console.error('获取歌曲信息失败:', error);
        }
      }
    };
    
    initializePlayback();
  }, []);

  // 更新搜索状态的函数
  const updateSearchState = (updates) => {
    setSearchState(prev => ({ ...prev, ...updates }));
  };

  // 导航到新页面并更新历史记录
  const navigateToPage = (page, playlistId = null) => {
    console.log('Navigating to page:', page, 'Current page:', currentPage); // 调试日志
    
    // 如果当前页面就是目标页面，不做任何操作
    if (page === currentPage && (page !== 'library' || playlistId === librarySelectedPlaylist)) {
      return;
    }

    // 创建新的历史记录
    const newHistory = pageHistory.slice(0, historyIndex + 1);
    const pageInfo = playlistId ? `${page}:${playlistId}` : page;
    newHistory.push(pageInfo);
    
    setPageHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setCurrentPage(page);
    
    if (playlistId) {
      setLibrarySelectedPlaylist(playlistId);
    }
  };

  // 页面导航函数 - 包装原有的页面切换逻辑
  const handlePageChange = (page) => {
    navigateToPage(page);
  };

  // 跳转到音乐库指定歌单的函数
  const navigateToLibrary = (playlistId) => {
    navigateToPage('library', playlistId);
  };

  // 上一页功能
  const goBack = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const pageInfo = pageHistory[newIndex];
      
      setHistoryIndex(newIndex);
      
      if (pageInfo.includes(':')) {
        const [page, playlistId] = pageInfo.split(':');
        setCurrentPage(page);
        setLibrarySelectedPlaylist(playlistId);
      } else {
        setCurrentPage(pageInfo);
      }
    }
  };

  // 下一页功能
  const goForward = () => {
    if (historyIndex < pageHistory.length - 1) {
      const newIndex = historyIndex + 1;
      const pageInfo = pageHistory[newIndex];
      
      setHistoryIndex(newIndex);
      
      if (pageInfo.includes(':')) {
        const [page, playlistId] = pageInfo.split(':');
        setCurrentPage(page);
        setLibrarySelectedPlaylist(playlistId);
      } else {
        setCurrentPage(pageInfo);
      }
    }
  };

  // 播放队列管理
  const handleRemoveFromQueue = (index) => {
    const updatedQueue = dataManager.removeFromPlayQueue(index);
    setPlayQueue(updatedQueue);
    
    // 如果删除的是当前播放的歌曲
    if (index === currentSongIndex) {
      if (updatedQueue.length === 0) {
        setCurrentSongIndex(-1);
        setCurrentPlayingSong(null);
        setIsPlaying(false);
        setSongInfo(null);
      } else {
        // 播放下一首（如果存在）
        const nextIndex = index < updatedQueue.length ? index : 0;
        setCurrentSongIndex(nextIndex);
        setCurrentPlayingSong(updatedQueue[nextIndex]);
        dataManager.setCurrentIndex(nextIndex);
      }
    } else if (index < currentSongIndex) {
      // 如果删除的歌曲在当前播放歌曲之前，调整索引
      const newIndex = currentSongIndex - 1;
      setCurrentSongIndex(newIndex);
      dataManager.setCurrentIndex(newIndex);
    }
  };

  const handleClearQueue = () => {
    dataManager.clearPlayQueue();
    setPlayQueue([]);
    setCurrentSongIndex(-1);
    setCurrentPlayingSong(null);
    setIsPlaying(false);
    setSongInfo(null);
  };

  // 播放模式切换
  const handlePlayModeChange = (mode) => {
    setPlayMode(mode);
    const settings = dataManager.getSettings();
    dataManager.saveSettings({ ...settings, playMode: mode });
  };

  // 下一曲
  const handleNextSong = async () => {
    if (playQueue.length === 0) return;

    let nextIndex;
    if (playMode === 'shuffle') {
      // 随机播放
      do {
        nextIndex = Math.floor(Math.random() * playQueue.length);
      } while (nextIndex === currentSongIndex && playQueue.length > 1);
    } else {
      // 顺序播放或循环播放
      nextIndex = currentSongIndex + 1;
      if (nextIndex >= playQueue.length) {
        nextIndex = playMode === 'repeat' ? 0 : currentSongIndex;
      }
    }

    if (nextIndex !== currentSongIndex && nextIndex < playQueue.length) {
      await playSong(playQueue[nextIndex], nextIndex);
    }
  };

  // 上一曲
  const handlePrevSong = async () => {
    if (playQueue.length === 0) return;

    let prevIndex;
    if (playMode === 'shuffle') {
      // 随机播放
      do {
        prevIndex = Math.floor(Math.random() * playQueue.length);
      } while (prevIndex === currentSongIndex && playQueue.length > 1);
    } else {
      // 顺序播放或循环播放
      prevIndex = currentSongIndex - 1;
      if (prevIndex < 0) {
        prevIndex = playMode === 'repeat' ? playQueue.length - 1 : 0;
      }
    }

    if (prevIndex !== currentSongIndex && prevIndex >= 0) {
      await playSong(playQueue[prevIndex], prevIndex);
    }
  };

  // 歌曲播放结束
  const handleSongEnd = async () => {
    if (playMode === 'single') {
      // 单曲循环 - 重新播放当前歌曲
      setIsPlaying(true);
    } else {
      // 自动播放下一曲
      await handleNextSong();
    }
  };

  // 播放歌曲的函数（支持队列索引）
  const playSong = async (song, queueIndex = null) => {
    // 如果指定了队列索引，直接播放队列中的歌曲
    if (queueIndex !== null) {
      setCurrentSongIndex(queueIndex);
      setCurrentPlayingSong(song);
      setIsPlaying(true);
      dataManager.setCurrentIndex(queueIndex);
      dataManager.addToRecent(song);
      
      // 获取歌曲详细信息
      try {
        const response = await fetch(
          `https://music-dl.sayqz.com/api/?source=${song.platform}&type=info&id=${song.id}`
        );
        const data = await response.json();
        if (data.code === 200) {
          setSongInfo(data.data);
        }
      } catch (error) {
        console.error('获取歌曲信息失败:', error);
      }
      return;
    }

    // 否则添加到队列并播放
    const updatedQueue = dataManager.addToPlayQueue(song);
    const newIndex = updatedQueue.length - 1;
    
    setPlayQueue(updatedQueue);
    setCurrentSongIndex(newIndex);
    setCurrentPlayingSong(song);
    setIsPlaying(true);
    dataManager.setCurrentIndex(newIndex);
    
    // 添加到最近播放
    dataManager.addToRecent(song);
    
    // 获取歌曲详细信息
    try {
      const response = await fetch(
        `https://music-dl.sayqz.com/api/?source=${song.platform}&type=info&id=${song.id}`
      );
      const data = await response.json();
      if (data.code === 200) {
        setSongInfo(data.data);
      }
    } catch (error) {
      console.error('获取歌曲信息失败:', error);
    }
  };

  // 批量添加歌曲到队列
  const addSongsToQueue = async (songs, replace = false) => {
    let updatedQueue;
    
    if (replace) {
      // 替换模式：清空队列后添加新歌曲
      dataManager.clearPlayQueue();
      updatedQueue = dataManager.addSongsToPlayQueue(songs);
      setPlayQueue(updatedQueue);
      
      // 从第一首歌开始播放
      if (songs.length > 0) {
        setCurrentSongIndex(0);
        setCurrentPlayingSong(songs[0]);
        setIsPlaying(true);
        dataManager.setCurrentIndex(0);
        dataManager.addToRecent(songs[0]);
        
        // 获取第一首歌的详细信息用于播放
        try {
          const response = await fetch(
            `https://music-dl.sayqz.com/api/?source=${songs[0].platform}&type=info&id=${songs[0].id}`
          );
          const data = await response.json();
          if (data.code === 200) {
            setSongInfo(data.data);
          }
        } catch (error) {
          console.error('获取歌曲信息失败:', error);
        }
      }
    } else {
      // 添加模式：直接添加到队列末尾
      updatedQueue = dataManager.addSongsToPlayQueue(songs);
      setPlayQueue(updatedQueue);
    }
  };

  // 打开播放页面的函数
  const openPlayerPage = (page = 'player') => {
    if (page === 'queue') {
      navigateToPage('queue');
    } else if (currentPlayingSong) {
      navigateToPage('player');
    }
  };

  // 根据当前页面渲染对应组件
  const renderMainContent = () => {
    switch (currentPage) {
      case 'search':
        return (
          <SearchPage 
            searchState={searchState}
            updateSearchState={updateSearchState}
            onPlaylistsChange={loadPlaylists}
            onPlaySong={playSong}
            onAddSongsToQueue={addSongsToQueue}
          />
        );
      case 'library':
        return (
          <LibraryPage 
            initialSelectedPlaylist={librarySelectedPlaylist}
            onPlaylistChange={setLibrarySelectedPlaylist}
            onPlaylistsChange={loadPlaylists}
            onPlaySong={playSong}
            onAddSongsToQueue={addSongsToQueue}
          />
        );
      case 'settings':
        return <SettingsPage />;
      case 'player':
        return (
          <PlayerPage 
            song={currentPlayingSong}
            songInfo={songInfo}
            currentPage={currentPage}
            onClose={() => goBack()}
          />
        );
      case 'queue':
        return (
          <QueuePage
            playQueue={playQueue}
            currentSongIndex={currentSongIndex}
            isPlaying={isPlaying}
            onPlaySong={async (index) => await playSong(playQueue[index], index)}
            onRemoveFromQueue={handleRemoveFromQueue}
            onClearQueue={handleClearQueue}
            playMode={playMode}
            onPlayModeChange={handlePlayModeChange}
          />
        );
      case 'home':
      default:
        return <MainContent />;
    }
  };

  return (
    <div className="h-screen bg-dark-50 text-dark-900 overflow-hidden">
      <div className="flex h-full">
        {/* 侧边栏 */}
        <Sidebar 
          currentPage={currentPage} 
          onPageChange={handlePageChange}
          onNavigateToLibrary={navigateToLibrary}
          librarySelectedPlaylist={librarySelectedPlaylist}
          userPlaylists={userPlaylists}
          onPlaylistsChange={loadPlaylists}
        />
        
        {/* 主要内容区域 */}
        <div className="flex-1 flex flex-col">
          {/* 顶部导航 */}
          <Header 
            canGoBack={historyIndex > 0}
            canGoForward={historyIndex < pageHistory.length - 1}
            onGoBack={goBack}
            onGoForward={goForward}
            onNavigateToSettings={() => navigateToPage('settings')}
          />
          
          {/* 主内容区 */}
          {renderMainContent()}
          
          {/* 底部播放器 */}
                           <MusicPlayer 
                   currentSong={currentPlayingSong}
                   songInfo={songInfo}
                   isPlaying={isPlaying}
                   onPlayPause={() => setIsPlaying(!isPlaying)}
                   onOpenPlayer={openPlayerPage}
                   playMode={playMode}
                   onPlayModeChange={handlePlayModeChange}
                   onNextSong={handleNextSong}
                   onPrevSong={handlePrevSong}
                   onSongEnd={handleSongEnd}
                   hasNext={currentSongIndex < playQueue.length - 1}
                   hasPrev={currentSongIndex > 0}
                 />
        </div>
      </div>
    </div>
  );
}

export default App;
